import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Lightbulb, Minus } from "lucide-react";

import { BarChart, Card, CardHeader, ErrorState, Skeleton, TrendChart } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import {
  analyticsRepository,
  type Insight,
  type PeriodSummary,
} from "@/repositories/analyticsRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * What a shopkeeper opens the app to see.
 *
 * Ordered by the question people actually arrive with: *what did I take today*,
 * then *is that better or worse than usual*, then *what is the shape of the
 * month*, then *what should I do about it*. Everything else is further down or
 * on the Reports screen.
 *
 * **One request.** Six separate calls would be six round trips on a township 3G
 * link, and the page would render in pieces — takings first, then the chart
 * moving and the numbers jumping a second later. One payload arrives whole.
 *
 * Cost and profit are absent from the payload entirely for anybody without
 * `reports.view_margin`, so the cards read them defensively rather than the
 * screen deciding what to hide. A field the browser never receives cannot be
 * revealed by opening a network tab.
 */
export function DashboardPage() {
  const canSeeMargin = useAuthStore((s) => s.can("reports.view_margin"));

  const overview = useQuery({
    queryKey: ["analytics", "overview"],
    queryFn: () => analyticsRepository.overview(30),
    // The dashboard is the screen most likely to be left open on a back-office
    // laptop all day. A minute is often enough to catch the morning's trading.
    refetchInterval: 60_000,
  });

  const insights = useQuery({
    queryKey: ["analytics", "insights"],
    queryFn: analyticsRepository.insights,
    staleTime: 5 * 60_000,
  });

  if (overview.isError) {
    return (
      <ErrorState
        title="Could not load your figures"
        description="The server did not answer. Your sales are unaffected — this screen only reads."
        onRetry={() => void overview.refetch()}
      />
    );
  }

  const data = overview.data;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Today"
          summary={data?.today}
          loading={overview.isLoading}
          canSeeMargin={canSeeMargin}
        />
        <Kpi
          label="This week"
          summary={data?.week}
          loading={overview.isLoading}
          canSeeMargin={canSeeMargin}
        />
        <Kpi
          label="This month"
          summary={data?.month}
          against={data?.month_before}
          loading={overview.isLoading}
          canSeeMargin={canSeeMargin}
        />
        <Card className="p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-500">Average basket</p>
          {overview.isLoading ? (
            <Skeleton className="mt-2 h-7 w-24" />
          ) : (
            <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">
              {formatMoney(data?.month.average_basket ?? "0")}
            </p>
          )}
          <p className="mt-1 text-xs text-ink-500">
            {data?.month.transaction_count ?? 0} sales this month
          </p>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Takings" subtitle="The last 30 days" />
          <div className="p-4 pt-0">
            {overview.isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <TrendChart
                caption="Daily takings over the last thirty days"
                points={(data?.trend ?? []).map((point) => ({
                  label: point.bucket,
                  value: point.gross_sales,
                }))}
              />
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="What the figures say" />
          <div className="space-y-3 p-4 pt-0">
            {insights.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (insights.data ?? []).length === 0 ? (
              <p className="text-sm text-ink-600">Nothing worth flagging right now.</p>
            ) : (
              (insights.data ?? []).map((insight) => (
                <InsightRow key={insight.code} insight={insight} />
              ))
            )}
          </div>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader title="Busiest hours" subtitle="Average takings per hour" />
          <div className="p-4 pt-0">
            <BarChart
              caption="Average takings by hour of the trading day"
              points={(data?.by_hour ?? []).map((row) => ({
                label: `${String(row.hour).padStart(2, "0")}:00`,
                value: row.average_per_day,
              }))}
            />
          </div>
        </Card>

        <Card>
          <CardHeader title="Best sellers" subtitle="By takings, last 30 days" />
          <div className="p-4 pt-0">
            <BarChart
              caption="Best-selling products by takings"
              points={(data?.top_products ?? []).map((row) => ({
                label: row.description,
                value: row.gross_sales,
              }))}
            />
          </div>
        </Card>

        <Card>
          <CardHeader title="How people pay" subtitle="Last 30 days" />
          <div className="p-4 pt-0">
            <BarChart
              caption="Takings by payment method"
              points={(data?.payment_mix ?? []).map((row) => ({
                label: METHOD_LABELS[row.method] ?? row.method,
                value: row.amount,
              }))}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

const METHOD_LABELS: Record<string, string> = {
  CASH: "Cash",
  CARD: "Card",
  TRANSFER: "Transfer",
  ACCOUNT: "On account",
  VOUCHER: "Voucher",
};

function Kpi({
  label,
  summary,
  against,
  loading,
  canSeeMargin,
}: {
  label: string;
  summary?: PeriodSummary | undefined;
  against?: PeriodSummary | undefined;
  loading: boolean;
  canSeeMargin: boolean;
}) {
  const change = movement(summary?.gross_sales, against?.gross_sales);

  return (
    <Card className="p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>

      {loading ? (
        <Skeleton className="mt-2 h-7 w-28" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums text-ink-900">
          {formatMoney(summary?.gross_sales ?? "0")}
        </p>
      )}

      <div className="mt-1 flex items-center gap-2 text-xs">
        {change && (
          <span
            className={cn(
              "flex items-center gap-0.5 font-medium",
              change.up ? "text-success-700" : "text-danger-700",
            )}
          >
            {change.up ? <ArrowUpRight size={13} aria-hidden /> : <ArrowDownRight size={13} aria-hidden />}
            {change.percent}%
          </span>
        )}
        {/* Only shown when the server sent it — which it does not for anybody
            without `reports.view_margin`. */}
        {canSeeMargin && summary?.gross_profit != null && (
          <span className="text-ink-500">{formatMoney(summary.gross_profit)} profit</span>
        )}
        {!change && !canSeeMargin && (
          <span className="flex items-center gap-0.5 text-ink-500">
            <Minus size={13} aria-hidden />
            {summary?.transaction_count ?? 0} sales
          </span>
        )}
      </div>
    </Card>
  );
}

function InsightRow({ insight }: { insight: Insight }) {
  const tone = {
    good: "text-success-700 bg-success-50",
    bad: "text-danger-700 bg-danger-50",
    neutral: "text-ink-600 bg-surface-2",
  }[insight.tone];

  return (
    <div className="flex gap-2.5">
      <span className={cn("mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full", tone)}>
        <Lightbulb size={13} aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-ink-900">{insight.title}</span>
        <span className="block text-xs text-ink-600">{insight.detail}</span>
      </span>
    </div>
  );
}

/**
 * Period-on-period change, or nothing.
 *
 * Nothing when there is no comparison to make. "Up ∞%" from a month with no
 * trading is not information, and a shopkeeper reading it once stops trusting
 * the rest of the card.
 */
function movement(now?: string, before?: string): { up: boolean; percent: string } | null {
  if (now == null || before == null) return null;

  const current = Number(now);
  const previous = Number(before);
  if (!Number.isFinite(current) || !Number.isFinite(previous) || previous <= 0) return null;

  const percent = ((current - previous) / previous) * 100;
  if (Math.abs(percent) < 1) return null;

  return { up: percent > 0, percent: Math.abs(percent).toFixed(0) };
}
